import { Button } from '@mui/material';

import {
  latestReport,
  useBankDossier,
} from '../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../api/workOrders/types';
import { Constants } from '../../../constants/bankDossier';
import {
  IconBankMS,
  IconHubMS,
  IconPictureAsPdfMS,
} from '../../../constants/icons';
import {
  EMPTY,
  subStatusTone,
  textOrEmpty,
} from '../../../helpers/bankDossier';
import { openExternal } from '../../../helpers/openExternal';
import useSnackbar from '../../../hooks/useSnackbar';
import { Money } from '../../UI/Money';
import { StatusPill } from '../../UI/StatusPill';
import { requestOpenDossierSection } from '../events';
import {
  Actions,
  Head,
  HeadTitle,
  Item,
  ItemHint,
  ItemLabel,
  ItemValue,
  Items,
  Root,
} from './styles';

interface DossierStripProps {
  propertyData: WorkOrderData | undefined;
}

/**
 * Compact identification strip shown under the page title of every work
 * order screen: the bank's own numbers, sub-status, channel and the appraisal
 * value, plus direct access to the latest report and the folder siblings.
 */
export function DossierStrip({
  propertyData,
}: DossierStripProps): JSX.Element | null {
  const dossier = useBankDossier(propertyData);
  const { handleSnackbar } = useSnackbar();

  if (!propertyData || !dossier) return null;

  const { strip } = Constants;
  const { identification: id } = dossier;
  const report = latestReport(dossier, propertyData.id);
  const relatedCount = dossier.related_requests.length;

  const openReport = (): void => {
    if (report?.url) {
      openExternal(report.url);
      return;
    }
    handleSnackbar(Constants.demoUnavailable, false);
  };

  return (
    <Root component="section" aria-label={strip.title}>
      <Head>
        <HeadTitle>
          {IconBankMS}
          {strip.title}
        </HeadTitle>
        <Actions>
          <Button
            variant="text"
            size="small"
            onClick={() => requestOpenDossierSection('related')}
          >
            {IconHubMS}
            {relatedCount > 0 ? strip.related(relatedCount) : strip.noRelated}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!report}
            onClick={openReport}
          >
            {IconPictureAsPdfMS}
            {report ? strip.latestReport : strip.noLatestReport}
          </Button>
        </Actions>
      </Head>
      <Items>
        <Item>
          <ItemLabel>{strip.control}</ItemLabel>
          <ItemValue mono title={id.bank_control_number ?? undefined}>
            {textOrEmpty(id.bank_control_number)}
          </ItemValue>
        </Item>
        <Item>
          <ItemLabel>{strip.folder}</ItemLabel>
          <ItemValue mono>{textOrEmpty(id.folder_number)}</ItemValue>
        </Item>
        <Item>
          <ItemLabel>{strip.request}</ItemLabel>
          <ItemValue>
            {id.request_ordinal ? strip.ordinal(id.request_ordinal) : EMPTY}
            {id.request_number && <ItemHint>nº {id.request_number}</ItemHint>}
          </ItemValue>
        </Item>
        <Item>
          <ItemLabel>{strip.subStatus}</ItemLabel>
          {id.sub_status ? (
            <div>
              <StatusPill tone={subStatusTone(id.sub_status)} dot>
                {id.sub_status}
              </StatusPill>
            </div>
          ) : (
            <ItemValue muted>{EMPTY}</ItemValue>
          )}
        </Item>
        <Item>
          <ItemLabel>{strip.channel}</ItemLabel>
          <ItemValue>{textOrEmpty(id.channel)}</ItemValue>
        </Item>
        <Item>
          <ItemLabel>{strip.appraisalValue}</ItemLabel>
          {dossier.appraisal_value != null ? (
            <ItemValue mono>
              <Money value={dossier.appraisal_value} />
            </ItemValue>
          ) : (
            <ItemValue muted>{strip.appraisalPending}</ItemValue>
          )}
        </Item>
      </Items>
    </Root>
  );
}
